<template>

  <FlashNotifications ref="flash" />

  <!-- ══════════════════════════════════════════════════════ -->
  <!-- MOBILE LAYOUT  (< lg) — native app feel               -->
  <!-- ══════════════════════════════════════════════════════ -->
  <div class="lg:hidden fixed inset-0 z-10 flex flex-col bg-gray-50 dark:bg-gray-950">

    <!-- Header -->
    <div class="shrink-0 flex items-center justify-between px-5 py-3.5 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800">
      <div class="flex items-center gap-2.5">
        <div class="w-8 h-8 bg-blue-600 rounded-xl flex items-center justify-center shrink-0">
          <svg class="h-[16px] w-auto" viewBox="54.57 148.46 39 39" xmlns="http://www.w3.org/2000/svg">
            <path fill="white" d="M 71.726562 162.816406 L 65.988281 162.816406 L 65.988281 159.667969 L 81.410156 159.667969 L 81.410156 162.816406 L 75.6875 162.816406 L 75.6875 175.976562 L 71.726562 175.976562 Z"/>
          </svg>
        </div>
        <span class="font-semibold text-gray-900 dark:text-white text-[15px] tracking-tight">{{ APPLICATION_NAME }}</span>
      </div>
      <div class="flex items-center gap-3">
        <span class="text-[11px] font-semibold text-blue-500 tabular-nums">{{ currentStep }}/2</span>
        <form :action="signOutPath" method="post">
          <input type="hidden" name="_method" value="delete" />
          <input type="hidden" name="authenticity_token" :value="requestHeaders['X-CSRF-TOKEN']" />
          <button type="submit" class="text-[13px] font-medium text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
            {{ I18N.website_content.app_nav.sign_out }}
          </button>
        </form>
      </div>
    </div>

    <!-- ── STEP 1: Profession picker ── -->
    <div v-if="currentStep === 1" class="flex-1 overflow-y-auto">
      <div class="px-4 pt-6 pb-6">
        <div class="px-1 mb-5">
          <h1 class="text-[22px] font-bold text-gray-900 dark:text-white tracking-tight leading-snug">What do you do?</h1>
          <p class="text-[13.5px] text-gray-400 dark:text-gray-500 mt-1">We'll suggest intake questions to match your work.</p>
        </div>

        <div class="flex flex-wrap gap-2">
          <button v-for="p in professions" :key="p.value"
            type="button"
            @click="selectProfession(p.value)"
            :class="profession === p.value
              ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
              : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700 hover:border-blue-300 hover:text-blue-600 hover:bg-blue-50/70 dark:hover:bg-blue-500/10 hover:shadow-sm hover:-translate-y-0.5'"
            class="px-4 py-2 rounded-full border text-[13.5px] font-medium transition-all duration-150 active:scale-95">
            {{ p.label }}
          </button>
        </div>

        <p class="mt-3 flex items-center gap-1.5 text-[12px] text-gray-400 dark:text-gray-500">
          <svg class="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 8h.01M11 12h1v4h1M12 21a9 9 0 100-18 9 9 0 000 18z"/>
          </svg>
          You won't be able to change this later from settings.
        </p>

        <div v-if="profession === 'other'" class="mt-4">
          <input type="text"
            ref="otherProfessionInputMobile"
            class="w-full bg-white dark:bg-gray-900 rounded-2xl px-5 py-3.5 text-[16px] text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none placeholder-gray-300 dark:placeholder-gray-600"
            placeholder="What's your profession?"
            v-model="other_profession" />
        </div>
      </div>
    </div>

    <!-- ── STEP 2: Account details ── -->
    <div v-if="currentStep === 2" class="flex-1 overflow-y-auto">
      <div class="px-4 pt-6 pb-6">

        <div class="px-1 mb-5">
          <h1 class="text-[22px] font-bold text-gray-900 dark:text-white tracking-tight leading-snug">Set up your account</h1>
          <p class="text-[13.5px] text-gray-400 dark:text-gray-500 mt-1">Takes less than a minute.</p>
        </div>

        <!-- Profile photo card -->
        <div class="bg-white dark:bg-gray-900 rounded-2xl px-5 py-4 mb-3 shadow-sm">
          <div class="flex items-center gap-4">
            <button type="button" @click="openProfilePictureModal"
                    class="relative shrink-0 focus:outline-none group" aria-label="Edit profile photo">
              <div class="w-[60px] h-[60px] rounded-full border-2 border-dashed border-gray-200 dark:border-gray-700 group-hover:border-blue-400 transition-all duration-200 flex items-center justify-center bg-gray-50 dark:bg-gray-800 overflow-hidden relative">
                <img v-if="profilePicturePreview" :src="profilePicturePreview" alt="" class="w-full h-full object-cover" />
                <span v-else class="text-lg font-semibold text-gray-400 dark:text-gray-500 select-none">{{ user.avatar_name }}</span>
                <div class="absolute inset-0 bg-black/25 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                  <svg class="w-4 h-4 fill-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M12 8c-2.168 0-4 1.832-4 4s1.832 4 4 4 4-1.832 4-4-1.832-4-4-4zm0 6c-1.065 0-2-.935-2-2s.935-2 2-2 2 .935 2 2-.935 2-2 2z"/>
                    <path d="M20 5h-2.586l-2.707-2.707A.996.996 0 0 0 14 2h-4a.996.996 0 0 0-.707.293L6.586 5H4c-1.103 0-2 .897-2 2v11c0 1.103.897 2 2 2h16c1.103 0 2-.897 2-2V7c0-1.103-.897-2-2-2zM4 18V7h3c.266 0 .52-.105.707-.293L10.414 4h3.172l2.707 2.707A.996.996 0 0 0 17 7h3l.002 11H4z"/>
                  </svg>
                </div>
              </div>
              <div class="absolute bottom-0 right-0 w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center ring-2 ring-white dark:ring-gray-900">
                <svg class="w-2 h-2 fill-white" viewBox="0 0 24 24"><path d="M19 11H13V5a1 1 0 00-2 0v6H5a1 1 0 000 2h6v6a1 1 0 002 0v-6h6a1 1 0 000-2z"/></svg>
              </div>
            </button>
            <div class="min-w-0">
              <p class="font-semibold text-gray-900 dark:text-white text-[15px] truncate">{{ user.full_name || 'Your Name' }}</p>
              <p class="text-[13px] text-blue-500 dark:text-blue-400 mt-0.5">
                {{ profilePicturePreview ? 'Tap to change photo' : 'Tap to add a photo' }}
              </p>
            </div>
          </div>
        </div>

        <!-- Name + URL card -->
        <div class="bg-white dark:bg-gray-900 rounded-2xl mb-3 shadow-sm divide-y divide-gray-100 dark:divide-gray-800">
          <div class="px-5 pt-4 pb-3.5">
            <label class="block text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2" for="m-full-name">
              {{ I18N.application.onboarding.full_name }}
            </label>
            <input id="m-full-name"
                   class="w-full text-[16px] font-medium text-gray-900 dark:text-white bg-transparent border-none outline-none focus:ring-0 p-0 placeholder-gray-300 dark:placeholder-gray-600"
                   type="text" placeholder="Jane Smith" required
                   v-model="user.full_name" />
            <p class="mt-1.5 text-xs text-red-500" v-if="errors.full_name">{{ errors.full_name }}</p>
          </div>
          <div class="px-5 pt-4 pb-3.5">
            <label class="block text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">
              {{ I18N.application.onboarding.url }}
            </label>
            <div class="flex items-center gap-1">
              <span class="text-[13px] text-gray-400 dark:text-gray-500 shrink-0">{{ APPLICATION_URL }}/</span>
              <input autocapitalize="off" autocorrect="off"
                     class="flex-1 min-w-0 text-[16px] font-medium text-gray-900 dark:text-white bg-transparent border-none outline-none focus:ring-0 p-0 placeholder-gray-300 dark:placeholder-gray-600"
                     type="text" placeholder="jane-smith"
                     v-model="user.url" v-on:input="urlUpdated" />
              <div class="shrink-0 ml-1">
                <svg v-if="errors.url" class="w-4 h-4 fill-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><path d="M310.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L160 210.7 54.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L114.7 256 9.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L160 301.3 265.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L205.3 256 310.6 150.6z"/></svg>
                <svg v-else-if="user.url" class="w-4 h-4 fill-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M470.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L192 338.7 425.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"/></svg>
              </div>
            </div>
            <p v-if="errors.url" class="mt-1.5 text-xs text-red-500">{{ errors.url }}</p>
            <p v-else-if="user.url" class="mt-1.5 text-[11px] text-blue-500 dark:text-blue-400 truncate">{{ APPLICATION_URL }}/{{ user.url }}</p>
          </div>
        </div>

        <!-- Timezone + time format card -->
        <div class="bg-white dark:bg-gray-900 rounded-2xl mb-3 shadow-sm divide-y divide-gray-100 dark:divide-gray-800">
          <div class="px-5 py-4 relative" @click.stop>
            <div class="flex items-center justify-between gap-3">
              <span class="text-[14px] font-medium text-gray-800 dark:text-gray-200 shrink-0">{{ I18N.application.onboarding.timezone }}</span>
              <button @click="toggleTimezoneResults" type="button"
                      class="flex items-center gap-1 text-[13px] text-blue-600 dark:text-blue-400 font-medium min-w-0"
                      :aria-expanded="String(!timezone_is_hidden)">
                <span class="truncate">{{ userTimeZoneFormatted }}</span>
                <svg class="w-4 h-4 shrink-0 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"/></svg>
              </button>
            </div>
            <div :class="{ 'hidden': timezone_is_hidden }"
                 class="absolute left-4 right-4 z-30 mt-2 bg-white dark:bg-gray-800 rounded-xl shadow-xl ring-1 ring-black/5 dark:ring-white/10 overflow-hidden flex flex-col max-h-56">
              <div class="flex items-center gap-2 border-b dark:border-gray-700 px-3 py-2 shrink-0">
                <svg class="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input autocomplete="off" class="w-full border-none bg-transparent text-sm py-0 focus:ring-0 dark:text-gray-200" placeholder="Search timezone" type="text" v-model="searchTimezoneTerm" />
              </div>
              <div class="overflow-y-auto flex-1">
                <button v-for="timezone in filteredTimeZones" :key="timezone.name"
                        class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-200 transition-colors"
                        type="button" @click="setUserTimeZone(timezone)">
                  <span class="shrink-0 rounded-full bg-gray-100 dark:bg-gray-700 px-1.5 py-px text-xs font-medium text-gray-600 dark:text-gray-300 tabular-nums">{{ timezone.offset }}</span>
                  <span class="flex-1 truncate">{{ timezone.name }}</span>
                  <span class="shrink-0 text-xs text-gray-400 tabular-nums">{{ timezone.time }}</span>
                </button>
              </div>
            </div>
          </div>
          <div class="px-5 py-4 flex items-center justify-between">
            <span class="text-[14px] font-medium text-gray-800 dark:text-gray-200">{{ I18N.website_content.account_settings.time_format }}</span>
            <select class="border-none !bg-white dark:!bg-gray-900 text-[14px] font-semibold text-blue-600 dark:text-blue-400 focus:ring-0 py-0 pr-5 text-right cursor-pointer appearance-none" v-model="user.time_format">
              <option value="12_hours">12h</option>
              <option value="24_hours">24h</option>
            </select>
          </div>
        </div>

        <!-- Working hours card -->
        <div class="bg-white dark:bg-gray-900 rounded-2xl px-5 py-4 mb-3 shadow-sm">
          <p class="text-[14px] font-medium text-gray-800 dark:text-gray-200 mb-3">
            {{ I18N.application.availabilities.default_availability.available_hours }}
          </p>
          <div class="flex items-center gap-3">
            <select class="flex-1 border border-gray-200 dark:border-gray-700 rounded-xl text-[14px] text-gray-900 dark:text-gray-100 dark:bg-gray-800 py-2.5 px-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    v-model="availability.start_at">
              <option v-for="hour in total_hours" :key="hour" :value="hour">{{ formattedHour(hour, user.time_format) }}</option>
            </select>
            <span class="text-gray-400 font-medium text-sm">—</span>
            <select class="flex-1 border border-gray-200 dark:border-gray-700 rounded-xl text-[14px] text-gray-900 dark:text-gray-100 dark:bg-gray-800 py-2.5 px-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    v-model="availability.end_at">
              <option v-for="hour in toHours(availability.start_at)" :key="hour" :value="hour">{{ formattedHour(hour, user.time_format) }}</option>
            </select>
          </div>
        </div>

        <!-- Working days card -->
        <div class="bg-white dark:bg-gray-900 rounded-2xl px-5 py-4 shadow-sm">
          <div class="flex items-center justify-between mb-3">
            <p class="text-[14px] font-medium text-gray-800 dark:text-gray-200">
              {{ I18N.application.availabilities.default_availability.available_days }}
            </p>
            <span class="text-[11px] tabular-nums font-semibold px-2 py-0.5 rounded-full"
                  :class="Object.values(availability.days).filter(Boolean).length === 0
                    ? 'bg-red-50 text-red-500'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'">
              {{ Object.values(availability.days).filter(Boolean).length }}/7
            </span>
          </div>
          <div class="grid grid-cols-7 gap-1.5">
            <button v-for="(day, key) in daysConfig" :key="key"
              type="button"
              @click="availability.days[key] = !availability.days[key]"
              :class="availability.days[key]
                ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700'"
              class="flex flex-col items-center py-2.5 border rounded-xl transition-all duration-150 text-center active:scale-95">
              <span class="text-[11px] font-semibold leading-none">{{ day }}</span>
            </button>
          </div>
        </div>

      </div>
    </div>

    <!-- Sticky footer -->
    <div class="shrink-0 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800 px-5 pt-4"
         style="padding-bottom:max(16px,env(safe-area-inset-bottom));box-shadow:0 -4px 24px rgba(0,0,0,0.06)">

      <!-- Step 1 footer -->
      <template v-if="currentStep === 1">
        <button :disabled="!canAdvanceFromStep1"
                :class="!canAdvanceFromStep1 ? 'opacity-40 cursor-not-allowed' : 'active:scale-[0.98]'"
                @click.prevent="goToStep2"
                class="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-2xl text-[16px] tracking-tight transition-all duration-150 flex items-center justify-center gap-2 shadow-lg shadow-blue-200 dark:shadow-blue-900/30"
                type="button">
          Continue
          <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"/>
          </svg>
        </button>
      </template>

      <!-- Step 2 footer -->
      <template v-if="currentStep === 2">
        <div class="flex items-center gap-3 mb-3">
          <button @click="goBack" type="button" data-testid="onboarding-back-btn"
                  class="flex items-center justify-center w-12 h-12 rounded-2xl border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors shrink-0">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"/>
            </svg>
          </button>
          <button :class="{ 'opacity-50 cursor-not-allowed': Object.keys(errors).length !== 0 }"
                  :disabled="Object.keys(errors).length !== 0"
                  @click.prevent="submitForm"
                  class="flex-1 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 active:scale-[0.98] text-white font-bold py-4 rounded-2xl text-[16px] tracking-tight transition-all duration-150 flex items-center justify-center gap-2 shadow-lg shadow-blue-200 dark:shadow-blue-900/30"
                  type="button">
            <span v-if="show_loading_in_submit_btn" class="flex items-center gap-2">
              <svg aria-hidden="true" class="w-5 h-5 animate-spin" viewBox="0 0 100 101" fill="none">
                <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" opacity="0.3"/>
                <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentColor"/>
              </svg>
              {{ I18N.application.onboarding.saving }}
            </span>
            <span v-else class="flex items-center gap-2">
              Create Profile
              <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"/>
              </svg>
            </span>
          </button>
        </div>
      </template>

    </div>

  </div>

  <!-- ══════════════════════════════════════════════════════ -->
  <!-- DESKTOP LAYOUT  (lg+) — form LEFT, brand RIGHT        -->
  <!-- ══════════════════════════════════════════════════════ -->
  <div class="hidden lg:flex flex-1 min-h-screen">

    <!-- ─── LEFT PANEL: Form ─── -->
    <div class="flex-1 bg-white dark:bg-gray-900 flex flex-col overflow-y-auto">
      <div class="flex-1 flex items-start lg:items-center justify-center px-6 sm:px-10 py-10 lg:py-12">
        <div class="w-full max-w-[440px] ob-enter">

          <!-- ── Step 1: Profession picker ── -->
          <template v-if="currentStep === 1">
            <div class="mb-7">
              <p class="text-[11px] font-semibold text-blue-500 uppercase tracking-wider mb-2">Step 1 of 2</p>
              <h2 class="text-[22px] font-bold text-gray-900 dark:text-white tracking-tight leading-snug">
                What do you do?
              </h2>
              <p class="text-gray-400 dark:text-gray-500 text-sm mt-1">
                We'll suggest intake questions to match your work.
              </p>
            </div>

            <div class="flex flex-wrap gap-2 mb-5">
              <button v-for="p in professions" :key="p.value"
                type="button"
                @click="selectProfession(p.value)"
                :class="profession === p.value
                  ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700 hover:border-blue-300 hover:text-blue-600 hover:bg-blue-50/70 dark:hover:bg-blue-500/10 hover:shadow-sm hover:-translate-y-0.5'"
                class="px-4 py-2 rounded-full border text-[13px] font-medium transition-all duration-150">
                {{ p.label }}
              </button>
            </div>

            <p class="mb-5 -mt-1.5 flex items-center gap-1.5 text-[12px] text-gray-400 dark:text-gray-500">
              <svg class="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 8h.01M11 12h1v4h1M12 21a9 9 0 100-18 9 9 0 000 18z"/>
              </svg>
              You won't be able to change this later from settings.
            </p>

            <div v-if="profession === 'other'" class="mb-5">
              <input type="text"
                ref="otherProfessionInputDesktop"
                class="form-input w-full !rounded-xl text-sm dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 focus:ring-blue-500"
                placeholder="What's your profession?"
                v-model="other_profession" />
            </div>

            <button :disabled="!canAdvanceFromStep1"
                    :class="!canAdvanceFromStep1 ? 'opacity-40 cursor-not-allowed' : 'hover:bg-blue-700 hover:shadow-blue-200 hover:shadow-md'"
                    @click.prevent="goToStep2"
                    class="w-full bg-blue-600 text-white font-semibold py-[11px] rounded-xl text-sm transition-all duration-150 shadow-sm flex items-center justify-center gap-2"
                    type="button">
              Continue
              <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"/>
              </svg>
            </button>

          </template>

          <!-- ── Step 2: Account details ── -->
          <template v-if="currentStep === 2">
            <div class="mb-8">
              <p class="text-[11px] font-semibold text-blue-500 uppercase tracking-wider mb-2">Step 2 of 2</p>
              <h2 class="text-[22px] font-bold text-gray-900 dark:text-white tracking-tight leading-snug">
                Let's set up your account
              </h2>
              <p class="text-gray-400 dark:text-gray-500 text-sm mt-1">
                Takes less than a minute. You can change everything later.
              </p>
            </div>

            <form v-on:submit.prevent="submitForm" class="space-y-5">

              <!-- Avatar -->
              <div class="flex justify-center pb-1">
                <button type="button" @click="openProfilePictureModal"
                        class="relative focus:outline-none group" aria-label="Add profile photo">
                  <div class="w-[72px] h-[72px] rounded-full border-[2.5px] border-dashed border-gray-200 dark:border-gray-600 group-hover:border-blue-400 transition-all duration-200 flex items-center justify-center bg-gray-50 dark:bg-gray-800 overflow-hidden relative">
                    <img v-if="profilePicturePreview" :src="profilePicturePreview" alt="" class="w-full h-full object-cover" />
                    <span v-else class="text-xl font-semibold text-gray-400 dark:text-gray-500 select-none">{{ user.avatar_name }}</span>
                    <div class="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                      <svg class="w-5 h-5 fill-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                        <path d="M12 8c-2.168 0-4 1.832-4 4s1.832 4 4 4 4-1.832 4-4-1.832-4-4-4zm0 6c-1.065 0-2-.935-2-2s.935-2 2-2 2 .935 2 2-.935 2-2 2z"/>
                        <path d="M20 5h-2.586l-2.707-2.707A.996.996 0 0 0 14 2h-4a.996.996 0 0 0-.707.293L6.586 5H4c-1.103 0-2 .897-2 2v11c0 1.103.897 2 2 2h16c1.103 0 2-.897 2-2V7c0-1.103-.897-2-2-2zM4 18V7h3c.266 0 .52-.105.707-.293L10.414 4h3.172l2.707 2.707A.996.996 0 0 0 17 7h3l.002 11H4z"/>
                      </svg>
                    </div>
                  </div>
                  <div class="absolute bottom-0 right-0 w-[22px] h-[22px] bg-blue-600 rounded-full flex items-center justify-center ring-[2.5px] ring-white dark:ring-gray-900 shadow-sm">
                    <svg class="w-2.5 h-2.5 fill-white" viewBox="0 0 24 24"><path d="M19 11H13V5a1 1 0 00-2 0v6H5a1 1 0 000 2h6v6a1 1 0 002 0v-6h6a1 1 0 000-2z"/></svg>
                  </div>
                </button>
              </div>
              <p class="text-center text-xs text-gray-400 dark:text-gray-500 -mt-2 pb-1">
                {{ profilePicturePreview ? 'Change photo' : 'Add a profile photo' }}
              </p>

              <!-- Full name -->
              <div>
                <label class="block text-[13px] font-medium text-gray-700 dark:text-gray-300 mb-1.5" for="user-full-name">
                  {{ I18N.application.onboarding.full_name }}
                </label>
                <input class="form-input w-full !rounded-xl text-sm dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 focus:ring-blue-500"
                       id="user-full-name" type="text" placeholder="Jane Smith" required
                       v-model="user.full_name" />
                <p class="mt-1.5 text-xs text-red-500" v-if="errors.full_name">{{ errors.full_name }}</p>
              </div>

              <!-- Booking URL -->
              <div>
                <label class="block text-[13px] font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  {{ I18N.application.onboarding.url }}
                </label>
                <div class="flex rounded-xl overflow-hidden border border-gray-300 dark:border-gray-700 transition-all focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                  <span class="hidden sm:inline-flex items-center px-3 py-2 bg-gray-50 dark:bg-gray-800 text-[12px] text-gray-500 dark:text-gray-400 border-r border-gray-300 dark:border-gray-700 whitespace-nowrap select-none">
                    {{ APPLICATION_URL }}/
                  </span>
                  <div class="relative flex-1">
                    <input autocapitalize="off" autocorrect="off"
                           class="w-full border-none bg-white dark:bg-gray-800 text-sm px-3 py-[9px] focus:outline-none focus:ring-0 dark:text-gray-100 placeholder-gray-400"
                           type="text" placeholder="jane-smith"
                           v-model="user.url" v-on:input="urlUpdated" />
                    <div class="absolute right-3 top-1/2 -translate-y-1/2">
                      <svg v-if="errors.url" class="w-4 h-4 fill-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><path d="M310.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L160 210.7 54.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L114.7 256 9.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L160 301.3 265.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L205.3 256 310.6 150.6z"/></svg>
                      <svg v-else-if="user.url" class="w-4 h-4 fill-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M470.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L192 338.7 425.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"/></svg>
                    </div>
                  </div>
                </div>
                <p v-if="errors.url" class="mt-1.5 text-xs text-red-500">{{ errors.url }}</p>
                <p v-else-if="user.url" class="mt-1.5 text-[12px] text-blue-500 dark:text-blue-400 truncate">
                  {{ APPLICATION_URL }}/{{ user.url }}
                </p>
              </div>

              <div class="border-t border-gray-100 dark:border-gray-800"></div>

              <!-- Timezone + Time format -->
              <div class="grid grid-cols-[1fr_100px] gap-3">
                <div>
                  <label class="block text-[13px] font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    {{ I18N.application.onboarding.timezone }}
                  </label>
                  <div class="relative" @click.stop>
                    <button @click="toggleTimezoneResults"
                            class="form-input flex w-full cursor-default items-center text-left text-sm dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 pr-8 !rounded-xl"
                            type="button" :aria-expanded="String(!timezone_is_hidden)">
                      <span class="truncate">{{ userTimeZoneFormatted }}</span>
                      <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-gray-400">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"/></svg>
                      </span>
                    </button>
                    <div :class="{ 'hidden': timezone_is_hidden }"
                         class="absolute z-30 mt-1 w-full min-w-[280px] max-h-52 bg-white dark:bg-gray-800 rounded-xl shadow-lg ring-1 ring-black/5 dark:ring-white/10 overflow-hidden flex flex-col">
                      <div class="flex items-center gap-2 border-b dark:border-gray-700 px-3 py-2 shrink-0">
                        <svg class="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                        <input autocomplete="off" class="w-full border-none bg-transparent text-sm py-0 focus:ring-0 dark:text-gray-200 dark:placeholder-gray-500"
                               placeholder="Search timezone" type="text" v-model="searchTimezoneTerm" />
                      </div>
                      <div class="overflow-y-auto flex-1">
                        <button v-for="timezone in filteredTimeZones" :key="timezone.name"
                                class="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-200 transition-colors"
                                type="button" @click="setUserTimeZone(timezone)">
                          <span class="shrink-0 rounded-full bg-gray-100 dark:bg-gray-700 px-1.5 py-px text-xs font-medium text-gray-600 dark:text-gray-300 tabular-nums">{{ timezone.offset }}</span>
                          <span class="flex-1 truncate">{{ timezone.name }}</span>
                          <span class="shrink-0 text-xs text-gray-400 tabular-nums">{{ timezone.time }}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <label class="block text-[13px] font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    {{ I18N.website_content.account_settings.time_format }}
                  </label>
                  <select class="form-input w-full text-sm !bg-white dark:!bg-gray-800 dark:border-gray-700 dark:text-gray-100 !rounded-xl" v-model="user.time_format">
                    <option value="12_hours">12h</option>
                    <option value="24_hours">24h</option>
                  </select>
                </div>
              </div>

              <!-- Working hours -->
              <div>
                <label class="block text-[13px] font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  {{ I18N.application.availabilities.default_availability.available_hours }}
                </label>
                <div class="flex items-center gap-2">
                  <select class="form-input flex-1 text-sm dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 !rounded-xl" v-model="availability.start_at">
                    <option v-for="hour in total_hours" :key="hour" :value="hour">{{ formattedHour(hour, user.time_format) }}</option>
                  </select>
                  <span class="text-gray-400 text-sm font-medium px-0.5">—</span>
                  <select class="form-input flex-1 text-sm dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 !rounded-xl" v-model="availability.end_at">
                    <option v-for="hour in toHours(availability.start_at)" :key="hour" :value="hour">{{ formattedHour(hour, user.time_format) }}</option>
                  </select>
                </div>
              </div>

              <!-- Working days -->
              <div>
                <div class="flex items-center justify-between mb-2">
                  <label class="text-[13px] font-medium text-gray-700 dark:text-gray-300">
                    {{ I18N.application.availabilities.default_availability.available_days }}
                  </label>
                  <span class="text-[11px] tabular-nums font-medium px-1.5 py-0.5 rounded-full"
                        :class="Object.values(availability.days).filter(Boolean).length === 0
                          ? 'bg-red-50 text-red-500'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'">
                    {{ Object.values(availability.days).filter(Boolean).length }}/7
                  </span>
                </div>
                <div class="grid grid-cols-7 gap-1.5">
                  <button v-for="(day, key) in daysConfig" :key="key"
                    type="button"
                    @click="availability.days[key] = !availability.days[key]"
                    :class="availability.days[key]
                      ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                      : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-blue-300 hover:text-blue-500'"
                    class="flex flex-col items-center py-2 border rounded-xl transition-all duration-150 text-center">
                    <span class="text-[11px] font-semibold leading-none">{{ day }}</span>
                  </button>
                </div>
              </div>

              <!-- Submit -->
              <div class="pt-1 flex items-center gap-3">
                <button @click="goBack" type="button" data-testid="onboarding-back-btn"
                        class="flex items-center justify-center w-11 h-11 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors shrink-0">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"/>
                  </svg>
                </button>
                <button :class="{ 'opacity-50 cursor-not-allowed': Object.keys(errors).length !== 0 }"
                        :disabled="Object.keys(errors).length !== 0"
                        class="flex-1 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-semibold py-[11px] rounded-xl text-sm transition-all duration-150 shadow-sm hover:shadow-blue-200 hover:shadow-md flex items-center justify-center gap-2"
                        type="submit">
                  <span v-if="show_loading_in_submit_btn" class="flex items-center gap-2">
                    <svg aria-hidden="true" class="w-4 h-4 animate-spin" viewBox="0 0 100 101" fill="none">
                      <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" opacity="0.3"/>
                      <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentColor"/>
                    </svg>
                    {{ I18N.application.onboarding.saving }}
                  </span>
                  <span v-else class="flex items-center gap-2">
                    Create Profile
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"/>
                    </svg>
                  </span>
                </button>
              </div>

            </form>
          </template>

        </div>
      </div>
    </div>

    <!-- ─── RIGHT PANEL: Brand ─── -->
    <div class="hidden lg:flex lg:w-[42%] xl:w-[38%] flex-shrink-0 relative overflow-hidden flex-col">
      <div class="absolute inset-0 bg-gradient-to-br from-blue-600 to-blue-800"></div>
      <div class="absolute inset-0 pointer-events-none" style="background-image:radial-gradient(rgba(255,255,255,.08) 1.5px,transparent 1.5px);background-size:30px 30px"></div>
      <div class="absolute -bottom-40 -left-40 w-[480px] h-[480px] rounded-full bg-blue-500/20 pointer-events-none" style="filter:blur(60px)"></div>
      <div class="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-blue-400/15 pointer-events-none" style="filter:blur(48px)"></div>

      <div class="relative flex flex-col w-full h-full px-10 xl:px-14 py-10">

        <!-- Logo -->
        <svg class="h-10 w-auto self-start shrink-0" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" zoomAndPan="magnify" preserveAspectRatio="xMidYMid meet" version="1.0" viewBox="54.57 148.46 139.82 38.29"><defs><g></g><clipPath id="licp"><path d="M 54.613281 148.5 L 92.863281 148.5 L 92.863281 186.75 L 54.613281 186.75 Z M 54.613281 148.5 " clip-rule="nonzero"></path></clipPath></defs><g clip-path="url(#licp)"><path fill="#ffffff" d="M 84.6875 186.699219 L 62.703125 186.699219 C 58.210938 186.699219 54.570312 183.058594 54.570312 178.566406 L 54.570312 156.59375 C 54.570312 152.101562 58.210938 148.457031 62.703125 148.457031 L 84.679688 148.457031 C 89.171875 148.457031 92.8125 152.101562 92.8125 156.59375 L 92.8125 178.566406 C 92.8125 183.058594 89.171875 186.699219 84.6875 186.699219 Z M 84.6875 186.699219 " fill-opacity="1" fill-rule="nonzero"></path></g><path fill="#2563eb" d="M 71.726562 162.816406 L 65.988281 162.816406 L 65.988281 159.667969 L 81.410156 159.667969 L 81.410156 162.816406 L 75.6875 162.816406 L 75.6875 175.976562 L 71.726562 175.976562 Z M 71.726562 162.816406 " fill-opacity="1" fill-rule="nonzero"></path><g fill="#ffffff" fill-opacity="1"><g transform="translate(98.801842, 175.900008)"><g><path d="M 11.5625 -16.25 L 11.203125 -14.09375 L 7.171875 -14.09375 L 7.171875 0 L 4.34375 0 L 4.34375 -14.09375 L 0.234375 -14.09375 L 0.234375 -16.25 Z M 11.5625 -16.25 "></path></g></g></g><g fill="#ffffff" fill-opacity="1"><g transform="translate(110.628051, 175.900008)"><g><path d="M 4.078125 0.09375 C 3.234375 0.09375 2.585938 -0.15625 2.140625 -0.65625 C 1.691406 -1.164062 1.46875 -1.816406 1.46875 -2.609375 L 1.46875 -11.953125 L 4.203125 -11.953125 L 4.203125 -2.859375 C 4.203125 -2.484375 4.253906 -2.234375 4.359375 -2.109375 C 4.472656 -1.984375 4.644531 -1.921875 4.875 -1.921875 C 4.9375 -1.921875 5.003906 -1.921875 5.078125 -1.921875 C 5.148438 -1.921875 5.21875 -1.925781 5.28125 -1.9375 C 5.34375 -1.9375 5.414062 -1.945312 5.5 -1.96875 L 5.78125 -0.234375 C 5.664062 -0.171875 5.523438 -0.117188 5.359375 -0.078125 C 5.210938 -0.0234375 5.03125 0.015625 4.8125 0.046875 C 4.59375 0.078125 4.347656 0.09375 4.078125 0.09375 Z M 2.875 -17.28125 C 3.382812 -17.28125 3.773438 -17.128906 4.046875 -16.828125 C 4.328125 -16.523438 4.46875 -16.128906 4.46875 -15.640625 C 4.46875 -15.160156 4.328125 -14.757812 4.046875 -14.4375 C 3.765625 -14.125 3.351562 -13.96875 2.8125 -13.96875 C 2.300781 -13.96875 1.90625 -14.125 1.625 -14.4375 C 1.34375 -14.75 1.203125 -15.128906 1.203125 -15.578125 C 1.203125 -16.066406 1.34375 -16.472656 1.625 -16.796875 C 1.90625 -17.117188 2.320312 -17.28125 2.875 -17.28125 Z M 2.875 -17.28125 "></path></g></g></g><g fill="#ffffff" fill-opacity="1"><g transform="translate(116.769042, 175.900008)"><g><path d="M 15.265625 0 L 15.265625 -7.828125 C 15.265625 -8.492188 15.160156 -9.023438 14.953125 -9.421875 C 14.742188 -9.816406 14.382812 -10.015625 13.875 -10.015625 C 13.570312 -10.015625 13.265625 -9.925781 12.953125 -9.75 C 12.640625 -9.582031 12.351562 -9.335938 12.09375 -9.015625 C 11.84375 -8.703125 11.632812 -8.320312 11.46875 -7.875 C 11.3125 -7.425781 11.234375 -6.929688 11.234375 -6.390625 L 11.234375 0 L 8.515625 0 L 8.515625 -7.828125 C 8.515625 -8.492188 8.410156 -9.023438 8.203125 -9.421875 C 7.992188 -9.816406 7.632812 -10.015625 7.125 -10.015625 C 6.800781 -10.015625 6.484375 -9.921875 6.171875 -9.734375 C 5.867188 -9.546875 5.59375 -9.273438 5.34375 -8.921875 C 5.09375 -8.578125 4.890625 -8.148438 4.734375 -7.640625 C 4.585938 -7.140625 4.515625 -6.578125 4.515625 -5.953125 L 4.515625 0 L 1.78125 0 L 1.78125 -6.046875 C 1.78125 -7.003906 1.710938 -7.804688 1.578125 -8.453125 C 1.453125 -9.109375 1.316406 -9.632812 1.171875 -10.03125 C 0.992188 -10.5 0.800781 -10.875 0.59375 -11.15625 L 2.765625 -12.03125 C 2.921875 -11.851562 3.078125 -11.632812 3.234375 -11.375 C 3.367188 -11.15625 3.5 -10.878906 3.625 -10.546875 C 3.75 -10.222656 3.851562 -9.851562 3.9375 -9.4375 C 4.332031 -10.394531 4.894531 -11.082031 5.625 -11.5 C 6.351562 -11.925781 7.125 -12.140625 7.9375 -12.140625 C 8.757812 -12.140625 9.394531 -11.929688 9.84375 -11.515625 C 10.300781 -11.097656 10.644531 -10.5625 10.875 -9.90625 C 11.300781 -10.707031 11.851562 -11.28125 12.53125 -11.625 C 13.21875 -11.96875 13.9375 -12.140625 14.6875 -12.140625 C 15.3125 -12.140625 15.832031 -12.039062 16.25 -11.84375 C 16.675781 -11.644531 17.015625 -11.363281 17.265625 -11 C 17.515625 -10.644531 17.691406 -10.210938 17.796875 -9.703125 C 17.910156 -9.203125 17.96875 -8.640625 17.96875 -8.015625 L 17.96875 0 Z M 15.265625 0 "></path></g></g></g><g fill="#ffffff" fill-opacity="1"><g transform="translate(136.17553, 175.900008)"><g><path d="M 0.84375 -5.6875 C 0.84375 -6.757812 0.988281 -7.703125 1.28125 -8.515625 C 1.582031 -9.335938 1.988281 -10.023438 2.5 -10.578125 C 3.019531 -11.128906 3.632812 -11.539062 4.34375 -11.8125 C 5.050781 -12.09375 5.8125 -12.234375 6.625 -12.234375 C 7.34375 -12.234375 7.953125 -12.144531 8.453125 -11.96875 C 8.960938 -11.800781 9.375 -11.5625 9.6875 -11.25 C 10.007812 -10.945312 10.242188 -10.585938 10.390625 -10.171875 C 10.546875 -9.753906 10.625 -9.304688 10.625 -8.828125 C 10.625 -8.253906 10.460938 -7.707031 10.140625 -7.1875 C 9.828125 -6.664062 9.367188 -6.207031 8.765625 -5.8125 C 8.171875 -5.425781 7.445312 -5.113281 6.59375 -4.875 C 5.75 -4.644531 4.800781 -4.523438 3.75 -4.515625 C 3.789062 -4.140625 3.863281 -3.785156 3.96875 -3.453125 C 4.070312 -3.128906 4.222656 -2.84375 4.421875 -2.59375 C 4.628906 -2.351562 4.890625 -2.160156 5.203125 -2.015625 C 5.523438 -1.867188 5.925781 -1.796875 6.40625 -1.796875 C 6.757812 -1.796875 7.132812 -1.835938 7.53125 -1.921875 C 7.9375 -2.003906 8.304688 -2.097656 8.640625 -2.203125 C 9.035156 -2.335938 9.425781 -2.484375 9.8125 -2.640625 L 10.53125 -0.9375 C 10.113281 -0.726562 9.664062 -0.546875 9.1875 -0.390625 C 8.769531 -0.242188 8.289062 -0.113281 7.75 0 C 7.207031 0.113281 6.632812 0.171875 6.03125 0.171875 C 5.09375 0.171875 4.296875 0.03125 3.640625 -0.25 C 2.984375 -0.53125 2.445312 -0.925781 2.03125 -1.4375 C 1.625 -1.945312 1.320312 -2.5625 1.125 -3.28125 C 0.9375 -4.007812 0.84375 -4.8125 0.84375 -5.6875 Z M 6.40625 -10.375 C 5.875 -10.375 5.4375 -10.25 5.09375 -10 C 4.75 -9.75 4.472656 -9.425781 4.265625 -9.03125 C 4.054688 -8.644531 3.910156 -8.207031 3.828125 -7.71875 C 3.742188 -7.226562 3.691406 -6.734375 3.671875 -6.234375 C 4.441406 -6.265625 5.09375 -6.363281 5.625 -6.53125 C 6.164062 -6.707031 6.601562 -6.914062 6.9375 -7.15625 C 7.269531 -7.40625 7.507812 -7.675781 7.65625 -7.96875 C 7.8125 -8.257812 7.890625 -8.546875 7.890625 -8.828125 C 7.890625 -9.296875 7.753906 -9.671875 7.484375 -9.953125 C 7.210938 -10.234375 6.851562 -10.375 6.40625 -10.375 Z M 6.40625 -10.375 "></path></g></g></g><g fill="#ffffff" fill-opacity="1"><g transform="translate(147.521969, 175.900008)"><g><path d="M 4.109375 0.09375 C 3.191406 0.09375 2.515625 -0.15625 2.078125 -0.65625 C 1.648438 -1.164062 1.4375 -1.8125 1.4375 -2.59375 L 1.4375 -16.921875 L 4.171875 -17.421875 L 4.171875 -2.859375 C 4.171875 -2.484375 4.242188 -2.222656 4.390625 -2.078125 C 4.535156 -1.941406 4.742188 -1.875 5.015625 -1.875 C 5.171875 -1.875 5.328125 -1.878906 5.484375 -1.890625 C 5.640625 -1.910156 5.78125 -1.9375 5.90625 -1.96875 C 6.050781 -2 6.175781 -2.03125 6.28125 -2.0625 L 6.625 -0.34375 C 6.414062 -0.257812 6.179688 -0.1875 5.921875 -0.125 C 5.703125 -0.0625 5.441406 -0.0078125 5.140625 0.03125 C 4.835938 0.0703125 4.492188 0.09375 4.109375 0.09375 Z M 4.109375 0.09375 "></path></g></g></g><g fill="#ffffff" fill-opacity="1"><g transform="translate(154.502544, 175.900008)"><g><path d="M 11.328125 -4.84375 C 11.328125 -4.363281 11.363281 -3.921875 11.4375 -3.515625 C 11.507812 -3.117188 11.585938 -2.722656 11.671875 -2.328125 C 11.765625 -1.941406 11.847656 -1.546875 11.921875 -1.140625 C 12.003906 -0.742188 12.046875 -0.304688 12.046875 0.171875 C 12.046875 1.867188 11.613281 3.128906 10.75 3.953125 C 9.882812 4.785156 8.71875 5.203125 7.25 5.203125 C 6.769531 5.203125 6.289062 5.15625 5.8125 5.0625 C 5.332031 4.976562 4.882812 4.878906 4.46875 4.765625 C 4.0625 4.660156 3.710938 4.550781 3.421875 4.4375 C 3.140625 4.320312 2.953125 4.242188 2.859375 4.203125 L 3.53125 2.109375 C 3.75 2.203125 4 2.300781 4.28125 2.40625 C 4.5625 2.507812 4.847656 2.609375 5.140625 2.703125 C 5.441406 2.804688 5.75 2.890625 6.0625 2.953125 C 6.382812 3.015625 6.703125 3.046875 7.015625 3.046875 C 7.910156 3.046875 8.554688 2.773438 8.953125 2.234375 C 9.347656 1.703125 9.546875 1.015625 9.546875 0.171875 C 9.546875 -0.597656 9.390625 -1.390625 9.078125 -2.203125 C 8.628906 -1.359375 8.03125 -0.753906 7.28125 -0.390625 C 6.539062 -0.0351562 5.757812 0.140625 4.9375 0.140625 C 3.675781 0.140625 2.773438 -0.222656 2.234375 -0.953125 C 1.703125 -1.691406 1.4375 -2.703125 1.4375 -3.984375 L 1.4375 -11.953125 L 4.171875 -11.953125 L 4.171875 -4.0625 C 4.171875 -3.382812 4.289062 -2.851562 4.53125 -2.46875 C 4.769531 -2.082031 5.179688 -1.890625 5.765625 -1.890625 C 6.066406 -1.890625 6.390625 -1.976562 6.734375 -2.15625 C 7.078125 -2.332031 7.390625 -2.578125 7.671875 -2.890625 C 7.953125 -3.203125 8.179688 -3.578125 8.359375 -4.015625 C 8.546875 -4.460938 8.640625 -4.941406 8.640625 -5.453125 L 8.640625 -11.953125 L 11.453125 -11.953125 Z M 11.328125 -4.84375 "></path></g></g></g><g fill="#ffffff" fill-opacity="1"><g transform="translate(167.408217, 175.900008)"><g><path d="M 1.6875 -16.25 C 1.789062 -16.25 2.023438 -16.25 2.390625 -16.25 C 2.765625 -16.257812 3.179688 -16.265625 3.640625 -16.265625 C 4.109375 -16.265625 4.585938 -16.269531 5.078125 -16.28125 C 5.578125 -16.289062 6.015625 -16.296875 6.390625 -16.296875 C 9.109375 -16.296875 11.125 -15.617188 12.4375 -14.265625 C 13.75 -12.910156 14.40625 -10.898438 14.40625 -8.234375 C 14.40625 -5.554688 13.734375 -3.503906 12.390625 -2.078125 C 11.046875 -0.660156 8.941406 0.046875 6.078125 0.046875 C 5.578125 0.046875 5.085938 0.0390625 4.609375 0.03125 C 4.140625 0.03125 3.707031 0.03125 3.3125 0.03125 C 2.925781 0.03125 2.585938 0.0234375 2.296875 0.015625 C 2.015625 0.00390625 1.8125 0 1.6875 0 Z M 6.046875 -14.15625 C 5.679688 -14.15625 5.375 -14.148438 5.125 -14.140625 C 4.875 -14.140625 4.675781 -14.140625 4.53125 -14.140625 L 4.53125 -2.109375 C 4.707031 -2.097656 4.9375 -2.09375 5.21875 -2.09375 C 5.5 -2.09375 5.90625 -2.09375 6.4375 -2.09375 C 8.113281 -2.09375 9.34375 -2.582031 10.125 -3.5625 C 10.90625 -4.550781 11.296875 -6.019531 11.296875 -7.96875 C 11.296875 -8.894531 11.203125 -9.738281 11.015625 -10.5 C 10.835938 -11.257812 10.535156 -11.910156 10.109375 -12.453125 C 9.691406 -12.992188 9.148438 -13.410156 8.484375 -13.703125 C 7.816406 -14.003906 7.003906 -14.15625 6.046875 -14.15625 Z M 6.046875 -14.15625 "></path></g></g></g><g fill="#ffffff" fill-opacity="1"><g transform="translate(182.832654, 175.900008)"><g><path d="M 6.3125 -12.234375 C 8.15625 -12.234375 9.488281 -11.6875 10.3125 -10.59375 C 11.144531 -9.507812 11.5625 -8.019531 11.5625 -6.125 C 11.5625 -5.226562 11.445312 -4.390625 11.21875 -3.609375 C 11 -2.835938 10.660156 -2.164062 10.203125 -1.59375 C 9.753906 -1.03125 9.1875 -0.582031 8.5 -0.25 C 7.8125 0.0703125 7.007812 0.234375 6.09375 0.234375 C 5.1875 0.234375 4.398438 0.0976562 3.734375 -0.171875 C 3.066406 -0.453125 2.515625 -0.859375 2.078125 -1.390625 C 1.648438 -1.921875 1.335938 -2.5625 1.140625 -3.3125 C 0.941406 -4.0625 0.84375 -4.910156 0.84375 -5.859375 C 0.84375 -6.765625 0.953125 -7.601562 1.171875 -8.375 C 1.398438 -9.15625 1.738281 -9.828125 2.1875 -10.390625 C 2.644531 -10.960938 3.21875 -11.410156 3.90625 -11.734375 C 4.59375 -12.066406 5.394531 -12.234375 6.3125 -12.234375 Z M 6.078125 -10.21875 C 5.609375 -10.21875 5.222656 -10.113281 4.921875 -9.90625 C 4.628906 -9.695312 4.398438 -9.40625 4.234375 -9.03125 C 4.066406 -8.65625 3.953125 -8.210938 3.890625 -7.703125 C 3.828125 -7.203125 3.796875 -6.664062 3.796875 -6.09375 C 3.796875 -5.5 3.832031 -4.9375 3.90625 -4.40625 C 3.976562 -3.882812 4.109375 -3.425781 4.296875 -3.03125 C 4.484375 -2.644531 4.738281 -2.335938 5.0625 -2.109375 C 5.394531 -1.890625 5.820312 -1.78125 6.34375 -1.78125 C 6.800781 -1.78125 7.175781 -1.882812 7.46875 -2.09375 C 7.769531 -2.300781 8.003906 -2.59375 8.171875 -2.96875 C 8.335938 -3.34375 8.453125 -3.773438 8.515625 -4.265625 C 8.578125 -4.765625 8.609375 -5.300781 8.609375 -5.875 C 8.609375 -6.46875 8.570312 -7.03125 8.5 -7.5625 C 8.4375 -8.09375 8.304688 -8.554688 8.109375 -8.953125 C 7.921875 -9.347656 7.660156 -9.65625 7.328125 -9.875 C 7.003906 -10.101562 6.585938 -10.21875 6.078125 -10.21875 Z M 6.078125 -10.21875 "></path></g></g></g></svg>

        <!-- Main copy -->
        <div class="flex-1 flex flex-col justify-center py-12">
          <p class="text-blue-300 text-[11px] font-semibold uppercase tracking-[0.14em] mb-5">Quick setup</p>
          <h1 class="text-[38px] xl:text-[44px] font-bold text-white leading-[1.12] mb-5">
            Your scheduling<br/>page, ready in<br/>under a minute.
          </h1>
          <p class="text-blue-200 text-[15px] leading-relaxed mb-10 max-w-[300px]">
            Share one link. Let people book time with you. No emails, no waiting.
          </p>
          <ul class="space-y-4">
            <li v-for="feature in features" :key="feature" class="flex items-center gap-3.5">
              <div class="w-6 h-6 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
                <svg class="w-3.5 h-3.5" fill="none" stroke="white" stroke-width="2.5" viewBox="0 0 24 24">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <span class="text-blue-100 text-[13.5px]">{{ feature }}</span>
            </li>
          </ul>
        </div>

        <!-- Sign out -->
        <div class="border-t border-white/15 pt-6">
          <form :action="signOutPath" method="post">
            <input type="hidden" name="_method" value="delete" />
            <input type="hidden" name="authenticity_token" :value="requestHeaders['X-CSRF-TOKEN']" />
            <button type="submit" class="flex items-center gap-2 text-blue-300 hover:text-white text-sm transition-colors">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75"/>
              </svg>
              {{ I18N.website_content.app_nav.sign_out }}
            </button>
          </form>
        </div>

      </div>
    </div>

  </div>

  <!-- Profile picture modal -->
  <div v-if="showProfilePictureModal"
       class="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50 backdrop-blur-sm"
       @click.self="showProfilePictureModal = false">
    <div class="relative w-full sm:max-w-sm bg-white dark:bg-gray-900 rounded-t-3xl sm:rounded-2xl border-0 sm:border border-gray-200 dark:border-gray-700 shadow-2xl ob-enter">
      <!-- Drag handle (mobile) -->
      <div class="flex justify-center pt-3 pb-1 sm:hidden">
        <div class="w-10 h-1 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
      </div>
      <div class="p-6 pt-4 sm:p-6">
        <h3 class="text-[15px] font-semibold text-gray-900 dark:text-gray-100 mb-1">
          {{ I18N.application.onboarding.change_profile_picture }}
        </h3>
        <p class="text-sm text-gray-400 dark:text-gray-500 mb-6">
          {{ parseLocale(I18N.application.onboarding.updated_subheading, {application_name: APPLICATION_NAME}) }}
        </p>

        <div class="flex items-center gap-5 mb-6">
          <div class="w-20 h-20 rounded-full border-2 border-dashed border-gray-200 dark:border-gray-700 overflow-hidden flex items-center justify-center bg-gray-50 dark:bg-gray-800 shrink-0">
            <img v-if="tempPreview" :src="tempPreview" alt="" class="w-full h-full object-cover" />
            <span v-else class="text-xl font-semibold text-gray-400 dark:text-gray-500 select-none">{{ user.avatar_name }}</span>
          </div>
          <div class="space-y-3">
            <label for="upload-input" class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer transition-colors font-medium">
              <svg class="w-4 h-4 fill-current shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <path d="M11 15h2V9h3l-4-5-4 5h3z"/><path d="M20 18H4v-7H2v7c0 1.103.897 2 2 2h16c1.103 0 2-.897 2-2v-7h-2v7z"/>
              </svg>
              {{ I18N.application.onboarding.upload_picture }}
            </label>
            <input id="upload-input" type="file" class="hidden" accept="image/*" @change="setProfilePicture" />
            <button @click="removeProfilePicture"
                    class="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors">
              <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 10 10">
                <path d="M.75 2.504h8.5M7.25 9.5h-4.5a1 1 0 0 1-1-1v-6h6.5v6a1 1 0 0 1-1 1ZM3.208 2.5v-.229a1.771 1.771 0 0 1 3.542 0V2.5M3.989 4.5v3M6.015 4.5v3" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              {{ I18N.application.onboarding.remove_picture }}
            </button>
          </div>
        </div>

        <div class="flex gap-2.5">
          <button @click="showProfilePictureModal = false" type="button"
                  class="flex-1 rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
            {{ I18N.application.bookings.upcoming.cancel }}
          </button>
          <button @click="uploadProfilePicture"
                  :disabled="!upload_profile_picture"
                  :class="{ 'opacity-50 cursor-not-allowed': !upload_profile_picture }"
                  type="button"
                  class="flex-1 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 transition-colors">
            <span v-if="show_loading_in_upload_btn" class="flex items-center justify-center gap-2">
              <svg aria-hidden="true" class="w-4 h-4 animate-spin" viewBox="0 0 100 101" fill="none">
                <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" opacity="0.3"/>
                <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentColor"/>
              </svg>
              {{ I18N.application.onboarding.saving }}
            </span>
            <span v-else>{{ I18N.website_content.account_settings.personal.save }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>

</template>

<script>
import axios from 'redaxios';
import Formatting from "controllers/vue_components/mixins/formatting"
import RequestMixin from "controllers/vue_components/mixins/request"
import Constants from "controllers/vue_components/mixins/constants"
import FlashNotifications from "controllers/vue_components/flash_toast"

export default {
    name: 'Onboarding',
    props: ['current_user', 'time_zone_list'],
    components: { FlashNotifications },
    mixins: [Formatting, RequestMixin, Constants],
    data() {
        return {
            currentStep: 1,
            profession: null,
            other_profession: '',
            professions: [
                { value: 'therapist',         label: 'Therapist' },
                { value: 'life_coach',         label: 'Life / Business Coach' },
                { value: 'personal_trainer',   label: 'Personal Trainer' },
                { value: 'nutritionist',       label: 'Nutritionist' },
                { value: 'massage_therapist',  label: 'Massage Therapist' },
                { value: 'physician',          label: 'Physician / Doctor' },
                { value: 'dentist',            label: 'Dentist' },
                { value: 'veterinarian',       label: 'Veterinarian' },
                { value: 'lawyer',             label: 'Lawyer / Attorney' },
                { value: 'financial_advisor',  label: 'Financial Advisor' },
                { value: 'accountant',         label: 'Accountant / CPA' },
                { value: 'real_estate_agent',  label: 'Real Estate Agent' },
                { value: 'photographer',       label: 'Photographer' },
                { value: 'hair_stylist',       label: 'Hair Stylist' },
                { value: 'tutor',              label: 'Tutor / Educator' },
                { value: 'interior_designer',  label: 'Interior Designer' },
                { value: 'it_consultant',      label: 'IT Consultant' },
                { value: 'hr_recruiter',       label: 'HR Recruiter' },
                { value: 'other',              label: 'Other' },
            ],
            show_loading_in_submit_btn: false,
            show_loading_in_upload_btn: false,
            selectedProfilePicture: null,
            profilePicturePreview: '',
            tempPreview: '',
            timezone_is_hidden: true,
            user: {},
            time_zones: [],
            searchTimezoneTerm: '',
            userTimeZoneFormatted: '',
            image_type_error: '',
            errors: {},
            upload_profile_picture: false,
            showProfilePictureModal: false,
            signOutPath: window.SIGN_OUT_PATH,
            availability: {
                start_at: 9,
                end_at: 17,
                days: {
                    sunday: false,
                    monday: true,
                    tuesday: true,
                    wednesday: true,
                    thursday: true,
                    friday: true,
                    saturday: false
                }
            },
            features: [
                'Custom booking URL, shareable anywhere',
                'Smart availability. No double bookings ever.',
                'Automatic reminders for you and your guests',
            ],
            daysConfig: {
                sunday:    'Su',
                monday:    'Mo',
                tuesday:   'Tu',
                wednesday: 'We',
                thursday:  'Th',
                friday:    'Fr',
                saturday:  'Sa',
            },
        };
    },
    computed: {
        canAdvanceFromStep1() {
            if (!this.profession) return false;
            if (this.profession === 'other') return this.other_profession.trim().length > 0;
            return true;
        },
        requestHeaders() {
            return {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('[name="csrf-token"]').getAttribute('content'),
            };
        },
        filteredTimeZones() {
            return this.time_zones.filter(zone => zone.name.toLowerCase().includes(this.searchTimezoneTerm.toLowerCase()));
        },
    },
    methods: {

        selectProfession(value) {
            this.profession = value;
            if (value === 'other') {
                this.$nextTick(() => {
                    const input = [this.$refs.otherProfessionInputMobile, this.$refs.otherProfessionInputDesktop]
                        .find(el => el && el.offsetParent !== null);
                    if (input) input.focus();
                });
            }
        },

        goToStep2() {
            this.currentStep = 2;
        },

        goBack() {
            this.currentStep = 1;
        },

        openProfilePictureModal() {
            this.initializeTempPreview();
            this.showProfilePictureModal = true;
        },

        async removeProfilePicture() {
            this.tempPreview = '';
            this.upload_profile_picture = true;
        },

        async submitForm() {
            this.errors = {};
            this.show_loading_in_submit_btn = true;
            try {
                let data = {
                    data: {
                        user: {
                            ...this.user,
                            profession: this.profession,
                            other_profession: this.other_profession,
                        },
                        availability: this.availability,
                    },
                };
                let response = await this.axiosRequest('POST', '/onboarding/create', data, false);

                if (response.js_error == false && response.response.success) {
                    window.location.replace("/events");
                } else {
                    const entries = Object.entries(response.response.data);
                    entries.forEach(([key, value]) => {
                        if (key == 'url' && value !== undefined) this.errors.url = value;
                        if (key == 'full_name' && value !== undefined) this.errors.full_name = value;
                    });
                    this.show_loading_in_submit_btn = false;
                }
            } catch (error) {
                this.show_loading_in_submit_btn = false;
                this.$refs.flash.error(this.I18N.notifications.something_went_wrong || 'Something went wrong. Please try again.');
            }
        },

        urlUpdated() {
            if (this.user.url === '') {
                this.errors.url = this.I18N.validations.url_cant_be_blank;
                return;
            }
            delete this.errors.url;
            clearTimeout(this._urlDebounceTimer);
            this._urlDebounceTimer = setTimeout(async () => {
                let res = await this.axiosRequest('POST', '/api/v1/users/validate_url_availability', { user: { url: this.user.url } }, false);
                if (res.js_error == false && res.response.success) {
                    if (res.response.data.available === false) {
                        this.errors.url = res.response.message;
                    }
                }
            }, 400);
        },

        async uploadProfilePicture() {
            if (!this.upload_profile_picture) return;
            this.show_loading_in_upload_btn = true;
            try {
                if (this.tempPreview == '') {
                    let res = await this.axiosRequest('DELETE', '/api/v1/users/remove_profile_picture', {}, false);
                    if (res.js_error == false && res.response.success) {
                        this.upload_profile_picture = false;
                        this.profilePicturePreview = '';
                        this.showProfilePictureModal = false;
                    }
                } else {
                    const formData = new FormData();
                    formData.append('profile_picture', this.selectedProfilePicture);
                    formData.append('authenticity_token', this.requestHeaders['X-CSRF-TOKEN']);
                    const res = await axios.post('/update_profile_picture', formData);
                    if (res.status === 200) {
                        this.profilePicturePreview = this.tempPreview;
                        this.showProfilePictureModal = false;
                    } else {
                        this.$refs.flash.error(this.I18N.notifications.something_went_wrong || 'Upload failed. Please try again.');
                    }
                }
            } catch (error) {
                this.$refs.flash.error(this.I18N.notifications.something_went_wrong || 'Upload failed. Please try again.');
            }
            this.show_loading_in_upload_btn = false;
        },

        resizeProfilePictureAndUpload(file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const image = new Image();
                image.src = e.target.result;
                image.onload = () => {
                    const canvas = document.createElement('canvas');
                    const context = canvas.getContext('2d');
                    const maxWidth = 500, maxHeight = 500;
                    let width = image.width, height = image.height;
                    if (width > height) {
                        if (width > maxWidth) { height *= maxWidth / width; width = maxWidth; }
                    } else {
                        if (height > maxHeight) { width *= maxHeight / height; height = maxHeight; }
                    }
                    canvas.width = width;
                    canvas.height = height;
                    context.drawImage(image, 0, 0, width, height);
                    canvas.toBlob((blob) => {
                        if (!blob) return;
                        this.selectedProfilePicture = blob;
                        this.upload_profile_picture = true;
                        this.tempPreview = URL.createObjectURL(blob);
                    }, 'image/jpeg', 0.8);
                };
            };
            reader.readAsDataURL(file);
        },

        initializeTempPreview() {
            this.tempPreview = this.profilePicturePreview;
        },

        setProfilePicture(event) {
            this.image_type_error = '';
            const new_file = event.target.files[0];
            if (!new_file) return;
            const allowedExtensions = /(\.jpg|\.jpeg|\.png|\.gif|\.avif|\.webp)$/i;
            if (!allowedExtensions.exec(new_file.name)) {
                this.image_type_error = this.I18N.notifications.settings.invalid_filetype;
                event.target.value = '';
                return false;
            }
            this.tempPreview = URL.createObjectURL(new_file);
            this.upload_profile_picture = true;
            this.resizeProfilePictureAndUpload(new_file);
            event.target.value = '';
        },

        toggleTimezoneResults() {
            this.timezone_is_hidden = !this.timezone_is_hidden;
        },

        setUserTimeZoneFormatted(user_object) {
            this.userTimeZoneFormatted = `(GMT${user_object.offset}) ${user_object.name}`;
        },

        setUserTimeZone(timezone) {
            this.user.timezone = timezone;
            this.setUserTimeZoneFormatted(timezone);
            this.timezone_is_hidden = true;
        },
    },
    mounted() {
        this._timezoneClickAway = () => { this.timezone_is_hidden = true; };
        document.addEventListener('click', this._timezoneClickAway);
    },
    unmounted() {
        document.removeEventListener('click', this._timezoneClickAway);
    },
    created() {
        this.user = JSON.parse(this.current_user);
        this.user.full_name = this.user.display_name;
        this.time_zones = JSON.parse(this.time_zone_list);
        if (this.user.timezone === null) {
            try {
                let user_timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                let user_city = user_timezone.split('/')[1].replace(/_/g, ' ');
                this.searchTimezoneTerm = user_city;
                this.user.timezone = this.filteredTimeZones[0];
                if (this.user.timezone === undefined) {
                    this.searchTimezoneTerm = 'UTC';
                    this.user.timezone = this.filteredTimeZones[0];
                }
            } catch (error) {
                this.searchTimezoneTerm = 'UTC';
                this.user.timezone = this.filteredTimeZones[0];
            }
        } else {
            this.searchTimezoneTerm = this.user.timezone;
            this.user.timezone = this.filteredTimeZones[0];
        }
        this.setUserTimeZoneFormatted(this.user.timezone);
        this.searchTimezoneTerm = '';
        this.availability = this.user.onboarding_data.availability;
        this.profilePicturePreview = this.user.profile_picture_url;
        this.tempPreview = this.profilePicturePreview;
    },
    watch: {
        'user.url'(newValue) {
            this.user.url = newValue.replace(/\s+/g, '-');
        },
        'user.full_name'(newValue) {
            if (newValue === null || newValue === undefined || newValue.trim() === '') {
                this.errors.full_name = this.I18N.validations.full_name_cant_be_blank;
            } else {
                delete this.errors.full_name;
            }
        },
    },
};
</script>
